import type { IObservationRating } from "src/app/cbta-web/services/offline-rating-calculation.service";
import {
	averageCompetencyRatings,
	calculateCompetencyRatingDetails,
	howOftenAttemptMetaFromCounts,
} from "./competency-scoring-v2";
import { applyV4PreviewPredefinedFilter } from "./original-obs-codes.utils";
import type {
	Ts1V4BreakdownHowManyInput,
	Ts1V4BreakdownHowManyRow,
	Ts1V4BreakdownHowOftenInput,
	Ts1V4BreakdownHowOftenRow,
	Ts1V4CalculatorInput,
} from "./offline-rating-calculator.types";

export type {
	Ts1V4BreakdownHowManyInput,
	Ts1V4BreakdownHowManyRow,
	Ts1V4BreakdownHowOftenInput,
	Ts1V4BreakdownHowOftenRow,
	Ts1V4CalculatorInput,
} from "./offline-rating-calculator.types";

/**
 * TS-1 v4 offline calculator — V2 competency scoring (base + uplift per competency).
 */
export class OfflineRatingV4Calculator {
	private filterObservations(
		predefinedObs: IObservationRating[],
		newObs: IObservationRating[],
		maneuverCodeToExclude: string[],
		observationDisabled: Ts1V4CalculatorInput["observationDisabled"],
	) {
		const isExcluded = (maneuverCode: string, observationCode: string) => {
			if (maneuverCodeToExclude?.includes(maneuverCode)) return true;
			return observationDisabled?.some(
				(d) => d.maneuverCode === maneuverCode && d.observationCode === observationCode,
			);
		};

		return {
			filteredPredefinedObs: predefinedObs?.filter(
				({ maneuverCode, observationCode }) => !isExcluded(maneuverCode, observationCode),
			),
			filteredNewObs: newObs?.filter(
				({ maneuverCode, observationCode }) => !isExcluded(maneuverCode, observationCode),
			),
		};
	}

	private ratingDetailsFromInput({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		observationDisabled = [],
		originalObsCodes,
	}: Ts1V4CalculatorInput) {
		const { filteredPredefinedObs, filteredNewObs } = this.filterObservations(
			predefinedObs,
			newObs,
			maneuverCodeToExclude ?? [],
			observationDisabled,
		);
		const { predefinedObs: effectivePredefined, formObservationCodes } = applyV4PreviewPredefinedFilter(
			filteredPredefinedObs ?? [],
			originalObsCodes ?? new Set(),
		);
		return calculateCompetencyRatingDetails({
			predefinedObs: effectivePredefined,
			newObs: filteredNewObs ?? [],
			formObservationCodes,
		});
	}

	calculateRatingA = (input: Ts1V4CalculatorInput): number => {
		const { pairs } = this.ratingDetailsFromInput(input);
		return averageCompetencyRatings(pairs, "ratingA");
	};

	calculateRatingB = (input: Ts1V4CalculatorInput): number => {
		const { pairs } = this.ratingDetailsFromInput(input);
		return averageCompetencyRatings(pairs, "ratingB");
	};

	ObservationBreakdownHowManyCalculation = (input: Ts1V4BreakdownHowManyInput): Ts1V4BreakdownHowManyRow[] => {
		const { pairs } = this.ratingDetailsFromInput({
			...input,
			observationDisabled: input.disabledObservations ?? [],
		});
		return pairs.map(({ obCategoryCode, ratingA }) => ({
			obCategoryCode,
			rating: ratingA,
		}));
	};

	ObservationBreakdownHowOftenCalculation = (input: Ts1V4BreakdownHowOftenInput): Ts1V4BreakdownHowOftenRow[] => {
		const { pairs, howOftenCounts } = this.ratingDetailsFromInput({
			...input,
			observationDisabled: input.disabledObservations ?? [],
		});
		return pairs.map(({ obCategoryCode, ratingB }) => ({
			obCategoryCode,
			rating: ratingB,
			...howOftenAttemptMetaFromCounts(howOftenCounts.get(obCategoryCode) ?? { e: 0, i: 0, ae: 0, ai: 0 }),
		}));
	};
}
